import React, { Fragment, useRef } from 'react';
import classnames from 'classnames';
import { loaders } from '../utils/loaders';

import { updateAddress, deleteAddress, addAddress } from '../actions/address';

import AddressBlock from '../components/user/AddressBlock.jsx';
import Badge from '../components/Badge.jsx';
import AddressForm from '../components/user/AddressForm.jsx';
import Loading from '../components/Loading.jsx';
import { useParams } from 'react-router-dom';

const UserAddresses = () => {
    const params = useParams();
    const [inProgress, setInProgress] = React.useState(true);
    const [addresses, setAddresses] = React.useState([]);
    const [currentAddress, setCurrentAddress] = React.useState(null);
    const _address_form = useRef();

    React.useEffect(() => {
        (async () => {
            let resp = await loaders.user_addresses.load(params.user_id);
            setInProgress(false);
            setAddresses(resp);
            setCurrentAddress(resp[0]);
        })();
    }, [params.user_id]);

    const selectAddress = (id) => {
        setCurrentAddress(addresses.find((a) => a._id === id));
    };

    const _addAddress = (e) => {
        let newAdr = {
            _id: 'new',
            street: [],
            region: '',
            city: '',
            country: '',
            postal_code: '',
            primary_shipping: false,
            primary_billing: false,
            customer_id: params.user_id
        };
        setAddresses((addresses) => [...addresses, newAdr]);
        setCurrentAddress(newAdr);
    };

    const saveAddress = async (e) => {
        e.preventDefault();

        let newAddress = _address_form.current.createObject();
        let primaryBilling = addresses.find((adr) => adr.primary_billing);
        let primaryShipping = addresses.find((adr) => adr.primary_shipping);
        newAddress.customer_id = params.user_id;

        if (newAddress.primary_billing && primaryBilling && primaryBilling._id !== currentAddress._id) {
            let billResp = await updateAddress(params.user_id, primaryBilling._id, { primary_billing: false });
            setAddresses((addresses) =>
                addresses.map((adr) => {
                    if (adr._id === billResp.data._id) {
                        return billResp.data;
                    }
                    return adr;
                })
            );
            setCurrentAddress(billResp.data);
            loaders.user_addresses.clear(params.user_id);
        }

        if (newAddress.primary_shipping && primaryShipping && primaryShipping._id !== currentAddress._id) {
            let shipResp = await updateAddress(params.user_id, primaryShipping._id, { primary_shipping: false });

            setAddresses((addresses) =>
                addresses.map((adr) => {
                    if (adr._id === shipResp.data._id) {
                        return shipResp.data;
                    }
                    return adr;
                })
            );
            setCurrentAddress(shipResp.data);
            loaders.user_addresses.clear(params.user_id);
        }

        if (currentAddress._id === 'new') {
            let newResp = await addAddress(params.user_id, newAddress);
            setAddresses((addresses) =>
                addresses.map((adr) => {
                    if (adr._id === 'new') {
                        return newResp.data;
                    }
                    return adr;
                })
            );
            setCurrentAddress(newResp.data);
            loaders.user_addresses.clear(params.user_id);
            return;
        }

        let resp = await updateAddress(params.user_id, currentAddress._id, newAddress);
        setAddresses((addresses) =>
            addresses.map((adr) => {
                if (adr._id === resp.data._id) {
                    return resp.data;
                }
                return adr;
            })
        );
        setCurrentAddress(resp.data);
        loaders.user_addresses.clear(params.user_id);
    };

    const removeAddress = async (e) => {
        e.preventDefault();

        if (addresses === 1) {
            window.alert('You must have at least one address.');
            return false;
        }

        if (currentAddress.primary_shipping) {
            window.alert('Please set another address as "Primary Shipping" before deleting.');
            return false;
        }

        if (currentAddress.primary_billing) {
            window.alert('Please set another address as "Primary Billing" before deleting.');
            return false;
        }

        await deleteAddress(params.user_id, currentAddress._id);
        let newAdrs = addresses.filter((adr) => adr._id !== currentAddress._id);
        setAddresses(newAdrs);
        setCurrentAddress(newAdrs[0]);
    };

    return (
        <Fragment>
            <div className="row middle-sm between-sm">
                <div className="col-xs-12 col-sm-6">
                    <h2>Addresses</h2>
                </div>
                <div className="col-xs-12 col-sm-3 end-sm">
                    <button type="button" className="button full-xs auto-sm" onClick={_addAddress}>
                        Add Address
                    </button>
                </div>
            </div>
            <hr />

            {inProgress && <Loading />}

            <div className="row">
                <div className="col-xs-12 col-md-4 address-list spaced-double">
                    {!inProgress && !addresses.length && <p>No Addresses</p>}
                    {addresses.length > 0 &&
                        addresses.map((adr, index) => {
                            return (
                                <div
                                    key={adr._id}
                                    id={adr._id}
                                    className={classnames('user-address', {
                                        active: currentAddress?._id === adr._id
                                    })}
                                >
                                    <div>
                                        <AddressBlock address={adr} name={adr.to} />
                                        <div className="badges">
                                            {adr.primary_billing && <Badge type="success" value="Primary Billing" />}
                                            {adr.primary_shipping && <Badge type="notice" value="Primary Shipping" />}
                                        </div>
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="button button-small button-info"
                                            onClick={() => selectAddress(adr._id)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                </div>
                <div className="col-xs-12 col-md-8">
                    <form className="user-address-form" onSubmit={saveAddress}>
                        {!!addresses.length && (
                            <div>
                                {addresses.map((adr) => {
                                    return (
                                        currentAddress?._id === adr._id && (
                                            <AddressForm
                                                key={`adr_${adr._id}`}
                                                ref={_address_form}
                                                flags
                                                address={adr}
                                            />
                                        )
                                    );
                                })}
                                <div>
                                    <button type="submit" className="button full-xs auto-md">
                                        Save
                                    </button>{' '}
                                    <button
                                        className="button button-danger full-xs auto-md"
                                        type="button"
                                        onClick={removeAddress}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default UserAddresses;
